import { SubjectLocation } from "../_objects/subject-model";

/**
 * Layouts an inspection structure can be exported with.
 * - legacy: one flat level of leaf folders named "NN_<identifier>", as IPLEX G series devices expect.
 * - tree: nested folders following the asset tree, named "NN_<display name>", as IPLEX One uses.
 */
export const INSPECTION_STRUCTURE_FORMAT = {
  LEGACY: "legacy",
  TREE: "tree"
} as const;

export type InspectionStructureFormat = (typeof INSPECTION_STRUCTURE_FORMAT)[keyof typeof INSPECTION_STRUCTURE_FORMAT];

/** One folder of an exported structure, with its path from the structure root (the inspection folder excluded). */
export interface StructureFolder {
  pathSegments: string[];
  location: SubjectLocation;
  isLeaf: boolean;
}

export const DATA_PLATE_FOLDER_INDEX = "00";

const NUMERIC_PREFIX = /^\d+_/;
const REPLACEMENT_CHAR = "_";
// Characters Windows refuses in a file or folder name, plus control characters
const WINDOWS_FORBIDDEN_CHARS = /[<>:"/\\|?*\p{Cc}]/gu;
// IPLEX devices only accept this subset of printable ASCII in a folder name (same set as OFFLINE_REGEX.File_Stat_Basename)
const DEVICE_FORBIDDEN_CHARS = /[^\x21\x23-\x29\x2b-\x2e\x30-\x39\x3b\x3d\x40-\x5b\x5d-\x7b\x7d-\x7e]/g;
// Windows silently drops trailing dots and spaces from a folder name
const TRAILING_DOTS_AND_SPACES = /[. ]+$/;

/**
 * Makes a display name usable as a folder name.
 * With `deviceSafe`, every character an IPLEX device rejects (spaces and non ASCII included) is replaced too.
 */
export function sanitizeStructureFolderName(name: string | null | undefined, deviceSafe = false): string {
  const forbidden = deviceSafe ? DEVICE_FORBIDDEN_CHARS : WINDOWS_FORBIDDEN_CHARS;
  return (name ?? "").trim().replace(forbidden, REPLACEMENT_CHAR).replace(TRAILING_DOTS_AND_SPACES, "");
}

/** Strips the "NN_" ordering prefix of an exported folder name. */
export function stripStructureFolderIndex(folderName: string | null | undefined): string {
  return (folderName ?? "").replace(NUMERIC_PREFIX, "");
}

/**
 * Comparison form of a folder name: index stripped, then reduced to the device safe character set,
 * so a name exported with either sanitizer still matches the location it was built from.
 */
export function normalizeStructureSegment(segment: string | null | undefined): string {
  return sanitizeStructureFolderName(stripStructureFolderIndex(segment), true);
}

/**
 * Lists the folders of an inspection structure in depth-first order, parents before children.
 * - legacy: leaf locations only, "NN_<identifier>" numbered across the whole tree, data plates get "00_".
 * - tree: every location, "NN_<display name>" numbered among its siblings, data plates get "00_".
 */
export function buildStructureFolders(locations: SubjectLocation[], format: InspectionStructureFormat, deviceSafe = false): StructureFolder[] {
  const folders: StructureFolder[] = [];

  if (format === INSPECTION_STRUCTURE_FORMAT.TREE) {
    collectTreeFolders(locations ?? [], [], deviceSafe, folders);
  } else {
    collectLegacyFolders(locations ?? [], { next: 1 }, folders);
  }

  return folders;
}

function collectLegacyFolders(locations: SubjectLocation[], counter: { next: number }, acc: StructureFolder[]): void {
  for (const location of locations) {
    if (hasChildren(location)) {
      collectLegacyFolders(location.children, counter, acc);
      continue;
    }

    const index = location.isDataPlate ? DATA_PLATE_FOLDER_INDEX : formatIndex(counter.next++);
    acc.push({ pathSegments: [`${index}_${location.identifier}`], location, isLeaf: true });
  }
}

function collectTreeFolders(locations: SubjectLocation[], parentSegments: string[], deviceSafe: boolean, acc: StructureFolder[]): void {
  let next = 1;

  for (const location of locations) {
    const index = location.isDataPlate ? DATA_PLATE_FOLDER_INDEX : formatIndex(next++);
    const label = sanitizeStructureFolderName(location.name, deviceSafe) || sanitizeStructureFolderName(location.identifier, deviceSafe);
    const pathSegments = [...parentSegments, `${index}_${label}`];

    acc.push({ pathSegments, location, isLeaf: !hasChildren(location) });
    collectTreeFolders(location.children ?? [], pathSegments, deviceSafe, acc);
  }
}

/**
 * Finds the leaf location a file belongs to from the folders it was stored in.
 *
 * The folder names may come from a legacy export (one "NN_<identifier>" folder), from a tree export
 * ("NN_<display name>" folders nested like the asset tree) or from a sub-folder of either, so the match
 * is tried from every starting folder, longest path first, anywhere in the tree. A folder name matches a
 * location by identifier, by legacy folder name or by display name; when sibling display names collide,
 * the "NN_" index decides. Only a leaf location is a valid destination.
 */
export function resolveStructureLocation(locations: SubjectLocation[], folderSegments: string[]): SubjectLocation | undefined {
  const segments = (folderSegments ?? []).map((segment) => segment.trim()).filter((segment) => segment.length > 0);
  const siblingGroups = collectSiblingGroups(locations ?? []);

  for (let offset = 0; offset < segments.length; offset++) {
    const rest = segments.slice(offset + 1);

    for (const group of siblingGroups) {
      for (const candidate of matchInSiblings(group, segments[offset])) {
        const leaf = walkDown(candidate, rest);
        if (leaf) {
          return leaf;
        }
      }
    }
  }

  return undefined;
}

function walkDown(location: SubjectLocation, rest: string[]): SubjectLocation | undefined {
  if (rest.length === 0) {
    return hasChildren(location) ? undefined : location;
  }

  for (const candidate of matchInSiblings(location.children ?? [], rest[0])) {
    const leaf = walkDown(candidate, rest.slice(1));
    if (leaf) {
      return leaf;
    }
  }

  return undefined;
}

function matchInSiblings(siblings: SubjectLocation[], segment: string): SubjectLocation[] {
  const stripped = stripStructureFolderIndex(segment);
  const normalized = normalizeStructureSegment(segment);

  if (stripped.length === 0) {
    return [];
  }

  const byIdentifier = siblings.filter(
    (location) =>
      location.identifier === stripped ||
      (!!location.folderName && stripStructureFolderIndex(location.folderName) === stripped) ||
      (normalized.length > 0 && normalizeStructureSegment(location.identifier) === normalized)
  );
  if (byIdentifier.length > 0) {
    return byIdentifier;
  }

  const byName = normalized.length > 0 ? siblings.filter((location) => normalizeStructureSegment(location.name) === normalized) : [];
  if (byName.length <= 1) {
    return byName;
  }

  const requestedIndex = Number(/^(\d+)_/.exec(segment)?.[1]);
  if (Number.isNaN(requestedIndex)) {
    return byName;
  }

  const indexes = siblingIndexes(siblings);
  const byIndex = byName.filter((location) => indexes.get(location) === requestedIndex);
  return byIndex.length > 0 ? byIndex : byName;
}

/** The "NN_" index each sibling gets in a tree export: data plates 0, the others numbered in order. */
function siblingIndexes(siblings: SubjectLocation[]): Map<SubjectLocation, number> {
  const indexes = new Map<SubjectLocation, number>();
  let next = 1;

  for (const location of siblings) {
    indexes.set(location, location.isDataPlate ? 0 : next++);
  }

  return indexes;
}

function collectSiblingGroups(locations: SubjectLocation[]): SubjectLocation[][] {
  const groups: SubjectLocation[][] = [];

  const visit = (siblings: SubjectLocation[]) => {
    if (siblings.length === 0) {
      return;
    }
    groups.push(siblings);
    siblings.forEach((location) => visit(location.children ?? []));
  };

  visit(locations);
  return groups;
}

function hasChildren(location: SubjectLocation): boolean {
  return (location.children?.length ?? 0) > 0;
}

function formatIndex(index: number): string {
  return String(index).padStart(2, "0");
}
